/// <reference path="../types.d.ts" />
import { api } from '../core/net';
import { UserContext, Auth } from './action-types';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { destroyToken, fetchConfig } from './_action-utilities';
import {
  loading,
  configAvailable,
  setActiveUser,
  loadingAction,
  setHomepagePreference
} from './ui-core.actions';
import { navigateContext } from './navigation.actions';
import { getEula, saveEula } from './eula-shared.actions';
import history from '../history';
import {
  authenticationContext,
  cmxLocation,
  HttpMethods,
  commonActionUtils,
  uiActions,
  launchPageActions,
  AmplifyCore,
  xcaliberActions,
  commonEnums
} from '@codametrix/ui-common';
import { saveServiceLine } from './service-lines';
import {
  getDefaultServiceLine,
  getHomepagePreference
} from '../core/preferences/preferences';
import { loadPreferences } from './user-preferences';
const { shimContext } = commonActionUtils;

const displayContexts = createAction<CMxAPI.OrganizationContext[]>(
  UserContext.DISPLAY_CONTEXTS
);

const tokenAvailable = createAction(Auth.TOKEN_AVAILABLE, (payload: CMxCommonApp.BearerToken) => ({
  payload,
  meta: { broadcast: false }
}));

const shouldRefresh = createAction(Auth.SHOULD_REFRESH, () => ({
  payload: undefined as void,
  meta: { broadcast: true }
}));

const _getConfigs = async (dispatch: any) => {
  const config = await fetchConfig();

  dispatch(configAvailable(config));
};

const fetchContexts = async (dispatch: any) => {
  const bootstrap = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`
  });
  await _getConfigs(dispatch);
  return bootstrap;
};

const postContextChoiceNavigation = async (
  context: CMx.Context,
  dispatch: any,
  acceptDefaultServiceLine: boolean = false,
  contextChoice?: CMx.JumpArgs
) => {
  const serviceLines = context.activeContext?.serviceLines || [];

  const userDefaults = await api<CMxAPI.Preference[]>({
    endpoint: `/userpreferences/${context.user.id}/v1`
  });

  const defaultServiceLineId = getDefaultServiceLine(userDefaults);
  const defaultServiceLine = serviceLines.find(
    sl => sl.id === defaultServiceLineId
  );

  const selectedServiceLine = serviceLines.find(
    sl => sl.id === contextChoice?.servicelineId
  );
  const homepagePreference = getHomepagePreference(userDefaults);
  dispatch(setHomepagePreference(homepagePreference));

  const chosenServiceLine =
    selectedServiceLine ?? defaultServiceLine ?? serviceLines[0];
  dispatch(saveServiceLine(chosenServiceLine));

  // Note: Case-details-layout configuration will be loaded on-demand when a case is opened
  const location = cmxLocation.getLocation();

  if (
    serviceLines.length > 1 &&
    !acceptDefaultServiceLine &&
    !defaultServiceLine
  ) {
    history.push('/choose/service-line/');
  } else if (location === '/eula/') {
    const eulaUpToDate = await getEula(
      context.user.id || -1,
      context.activeContext?.organizationId || -1
    );
    dispatch(saveEula(eulaUpToDate));
    history.push(location);
  } else {
    dispatch(navigateContext({ context, homepagePreference }));
  }
};

// there are two actions here that have very similar names
// selectContext and chooseContext.  chooseContext informs the ui
// that a context choice has been made.
// selectContext is the api sequence to tell the server that a context
// has been made.
// This has been split into two calls so that context choice can be made
// *prior* to further api calls being made.
const selectContext = createAsyncThunk<
  CMx.Context,
  CMx.OrganizationFormChoice,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.CHOOSE_CONTEXT, async (contextChoice, { dispatch }) => {
  dispatch(loading(true));
  dispatch(loadingAction({ isLoading: true }));
  const oldToken = authenticationContext.getToken();
  const contexts = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: contextChoice
  });

  const shimmed = shimContext(contexts);

  if (
    shimmed.activeContext &&
    (shimmed.activeContext.displayName === null ||
      shimmed.activeContext?.displayName === undefined)
  ) {
    shimmed.activeContext.displayName = shimmed.activeContext?.organizationName;
  }

  const token: CMxCommonApp.BearerToken = {
    token: `${shimmed.jwtToken}`,
    expiryTimeToLive: shimmed.expiryTimeToLive
  };
  dispatch(tokenAvailable(token));
  await destroyToken(oldToken);
  await dispatch(uiActions.chooseContext(shimmed));

  await postContextChoiceNavigation(shimmed, dispatch);

  dispatch(loading(false));
  dispatch(loadingAction({ isLoading: false }));
  return shimmed;
});

export const onlyRefreshContext = createAsyncThunk<
  void,
  CMx.JumpArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.ONLY_REFRESH_CONTEXT, async (contextChoice, { dispatch }) => {
  const freshContexts = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: { organizationId: contextChoice.organizationId }
  });

  const shimmed = shimContext(freshContexts);
  const token: CMxCommonApp.BearerToken = {
    token: `${shimmed.jwtToken}`,
    expiryTimeToLive: shimmed.expiryTimeToLive
  };

  dispatch(tokenAvailable(token));
  return;
});

const putContext = async (
  contextChoice: CMx.JumpArgs,
  dispatch: any
): Promise<CMx.Context> => {
  const oldToken = authenticationContext.getToken();
  const freshContexts = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: { organizationId: contextChoice.organizationId }
  });

  dispatch(setActiveUser(freshContexts.user));
  dispatch(launchPageActions.userDetailsAvailable(freshContexts) as any);

  const shimmed = shimContext(freshContexts);
  const token: CMxCommonApp.BearerToken = {
    token: `${shimmed.jwtToken}`,
    expiryTimeToLive: shimmed.expiryTimeToLive
  };

  dispatch(tokenAvailable(token));
  await destroyToken(oldToken);
  await _getConfigs(dispatch);
  await dispatch(uiActions.chooseContext(shimmed));

  return shimmed;
};

const refreshContext = createAsyncThunk<
  void,
  CMx.JumpArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.REFRESH_CONTEXT, async (contextChoice, { dispatch }) => {
  await putContext(contextChoice, dispatch);
});

const jumpContext = createAsyncThunk<
  CMx.Context,
  CMx.JumpArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.JUMP_CONTEXT, async (contextChoice, { dispatch }) => {
  dispatch(loading(true));
  // enable the new context
  const shimmed = await putContext(contextChoice, dispatch);
  if (shimmed.user.id) {
    dispatch(loadPreferences(shimmed.user.id));
  }
  // we are technically in the new context now, so we can stop "loading"
  // the awaitingResources state will keep the ui from displaying the new
  // context until the resources are loaded.
  dispatch(loading(false));

  // route the user to the appropriate page
  await postContextChoiceNavigation(shimmed, dispatch, true, contextChoice);

  // service line specific resources
  const { servicelineId } = contextChoice;
  const selectedServiceLine = shimmed.activeContext?.serviceLines?.find(
    serviceLine => serviceLine.id === servicelineId
  );
  const serviceLineName = selectedServiceLine
    ? selectedServiceLine.name
    : commonEnums.ServiceLines.ALL;

  // load provider directory if needed
  const hasProviderDirectory = contextChoice
    ? AmplifyCore.benchEditable.isResourceAllowed(
        (shimmed.activeContext as any) as CMxAPI.OrganizationContext,
        'PROVIDER_DIRECTORY',
        serviceLineName
      )
    : false;
  if (hasProviderDirectory) {
    await xcaliberActions.loadXCaliber(dispatch);
  }
  return shimmed;
});

export {
  fetchContexts,
  displayContexts,
  selectContext,
  putContext,
  shouldRefresh,
  refreshContext,
  jumpContext,
  tokenAvailable
};
